import { Tooltip, Dropdown, Checkbox } from "antd";
import { useEffect, useState } from "react";
import { Heart, Icon, Image, MessageSquare, Star, Tag } from "react-feather";
import { useTranslation } from "react-i18next";
import { FILTER_PHOTO } from "../../constants/commons";
import { TFilterPhotoKeys, TOption } from "../../types/common.type";

interface AlbumTag {
  id: number;
  name: string;
}

type TFilterAlbumProps = {
  setIsFilter: (value: any) => void;
  counts: Record<TFilterPhotoKeys, number>;
  tags?: AlbumTag[];
};

type TOptionsFilter = TOption<string> & {
  count: number;
  icon: any;
};

const ICONS: Record<TFilterPhotoKeys, Icon> = {
  all: Image,
  recommend: Star,
  like: Heart,
  comment: MessageSquare,
};

const FilterAlbum = (props: TFilterAlbumProps) => {
  const { setIsFilter, counts, tags = [] } = props;
  const { t } = useTranslation();
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  const optionFilter: TOptionsFilter[] = Object.keys(FILTER_PHOTO).map(
    (item) => ({
      value: FILTER_PHOTO[item],
      label: t(`albumDetail.filter.${item}`),
      icon: ICONS[item],
      count: counts[item],
    })
  );

  const onChangeFilter = (value: string) => {
    if (!value) {
      setCheckedList([]);
      return;
    }
    if (checkedList.includes(value)) {
      setCheckedList(checkedList.filter((item) => item !== value));
    } else setCheckedList([...checkedList, value]);
  };

  // Khi chọn tag
  const handleTagChange = (checkedValues: any) => {
    setSelectedTagIds(checkedValues);
    setTagDropdownOpen(false);
  };

  // Helper: chuyển checkedList sang filter object
  const getFilterFromCheckedList = () => {
    if (checkedList.length === 0) return {};
    return checkedList.reduce((a, v) => ({ ...a, [v]: 1 }), {});
  };

  useEffect(() => {
    if (!selectedTagIds.length) {
      if (checkedList.length === 0) {
        setIsFilter(null);
      } else {
        setIsFilter(getFilterFromCheckedList());
      }
    } else {
      setIsFilter({ ...getFilterFromCheckedList(), tag_ids: selectedTagIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedList, selectedTagIds]);

  const tagDropdown = (
    <div className="p-2 min-w-[180px] bg-white dark:bg-bg-secondary rounded-lg shadow">
      <div className="flex items-center justify-between mb-2">
        {tags.length > 0 && (
          <Checkbox
            indeterminate={
              selectedTagIds.length > 0 && selectedTagIds.length < tags.length
            }
            checked={selectedTagIds.length === tags.length}
            onChange={e => {
              if (e.target.checked) {
                setSelectedTagIds(tags.map(tag => tag.id));
              } else {
                setSelectedTagIds([]);
              }
            }}
          >
            {t('checkAll')}
          </Checkbox>
        )}
      </div>
      <Checkbox.Group
        style={{ width: '100%' }}
        value={selectedTagIds}
        onChange={handleTagChange}
      >
        {tags.length === 0 ? (
          <div className="text-gray-400 text-center py-4">{t('albumDetail.album.tagged')} (0)</div>
        ) : (
          tags.map((tag) => (
            <div key={tag.id} className="mb-2">
              <Checkbox value={tag.id}>{tag.name}</Checkbox>
            </div>
          ))
        )}
      </Checkbox.Group>
    </div>
  );

  return (
    <div className="flex sm:space-x-2 space-x-1 text-sm items-center flex-wrap sm:flex-nowrap">
      {optionFilter.map((item) => {
        return (
          <Tooltip title={item.label} key={item.value}>
            <div
              onClick={() => {
                onChangeFilter(item.value);
              }}
              className={`py-1 px-3 flex gap-1 items-center cursor-pointer transition-all rounded-3xl ${
                (checkedList.length === 0 && !item.value) ||
                checkedList.includes(item.value)
                  ? "text-secondary bg-[#0bcdac26] hover:bg-[#0bcdac26]"
                  : "hover:bg-gray-200/50"
              }`}
            >
              <item.icon className="w-4" />
              <div className="font-medium">{item.count}</div>
            </div>
          </Tooltip>
        );
      })}
      {/* Filter tag dropdown */}
      <Dropdown
        open={tagDropdownOpen}
        onOpenChange={setTagDropdownOpen}
        dropdownRender={() => tagDropdown}
        trigger={["click"]}
      >
        <Tooltip title={t('albumDetail.album.tagged')}>
          <div
            onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
            className={`${tags.length > 0 ? "py-1 px-3 flex gap-1 items-center cursor-pointer transition-all rounded-3xl" : "hidden"} ${
              selectedTagIds.length
                ? "text-secondary bg-[#0bcdac26] hover:bg-[#0bcdac26]"
                : "hover:bg-gray-200/50"
            }`}
          >
            <Tag className="w-4" />
            <div className="font-medium">{tags.length}</div>
          </div>
        </Tooltip>
      </Dropdown>
    </div>
  );
};

export default FilterAlbum;
